import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Radio, Clock, Users, ChevronRight, Tv } from "lucide-react";

/**
 * LiveMatchWidget — small "going live now / next" card for the Creator
 * Blueprint page. Pulls from /api/live (currently-live) and
 * /api/live/upcoming (scheduled), shows up to 3 of each, polls every 25s.
 * Self-hides when there's nothing to show so the page doesn't render an
 * empty card on a quiet day.
 */
function fmtCountdown(iso) {
  if (!iso) return "";
  const target = new Date(iso).getTime();
  const diff = target - Date.now();
  if (diff <= 0) return "now";
  const min = Math.floor(diff / 60000);
  if (min < 60) return `in ${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return `in ${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `in ${d}d`;
}

function fmtAge(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just started";
  if (diff < 3600) return `${Math.floor(diff / 60)}m on air`;
  return `${Math.floor(diff / 3600)}h on air`;
}

export default function LiveMatchWidget() {
  const [liveNow, setLiveNow] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const [a, b] = await Promise.allSettled([
          api.get("/live"),
          api.get("/live/upcoming"),
        ]);
        if (cancelled) return;
        const live = a.status === "fulfilled" ? (Array.isArray(a.value.data) ? a.value.data : (a.value.data?.streams || [])) : [];
        const next = b.status === "fulfilled" ? (Array.isArray(b.value.data) ? b.value.data : (b.value.data?.streams || [])) : [];
        setLiveNow(live.slice(0, 3));
        setUpcoming(next.filter((s) => s.scheduled_for).slice(0, 3));
      } catch (e) {
        // network/timeout — keep the previous state, just retry next tick
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    tick();
    const id = setInterval(tick, 25000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (loading) return null;
  if (liveNow.length === 0 && upcoming.length === 0) return null;

  return (
    <section data-testid="blueprint-live-match" className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="rounded-2xl bg-gradient-to-br from-red-900/30 via-black to-black ring-1 ring-red-500/30 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live Match
          </span>
          <h3 className="font-display font-black text-lg tracking-tight">On the network right now</h3>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {/* On Air column */}
          <div data-testid="blueprint-live-now-col">
            <div className="text-[10px] uppercase tracking-[0.25em] text-red-300 font-bold mb-2 flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5" /> On Air ({liveNow.length})
            </div>
            <div className="space-y-2">
              {liveNow.length === 0 ? (
                <div className="text-xs text-white/40 italic px-3 py-2 rounded-lg bg-white/5">No streams live right now — be the first.</div>
              ) : liveNow.map((s) => (
                <Link
                  key={s.id}
                  to={`/live/${s.id}`}
                  data-testid={`blueprint-live-row-${s.id}`}
                  className="group flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 ring-1 ring-white/10 hover:ring-red-500/50 transition"
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-black shrink-0">
                    {s.thumbnail_url ? (
                      <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-900 to-black">
                        <Tv className="w-4 h-4 text-white/50" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate group-hover:text-red-300 transition">{s.title}</div>
                    <div className="text-[10px] text-white/50 flex items-center gap-2 mt-0.5">
                      <span className="font-bold uppercase tracking-wider">{s.creator_name || "Creator"}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{(s.viewer_count || 0).toLocaleString()}</span>
                      <span>·</span>
                      <span>{fmtAge(s.started_at)}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-red-300 transition shrink-0" />
                </Link>
              ))}
            </div>
          </div>

          {/* Upcoming column */}
          <div data-testid="blueprint-upcoming-col">
            <div className="text-[10px] uppercase tracking-[0.25em] text-tv-accent font-bold mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Going Live Next ({upcoming.length})
            </div>
            <div className="space-y-2">
              {upcoming.length === 0 ? (
                <div className="text-xs text-white/40 italic px-3 py-2 rounded-lg bg-white/5">No streams scheduled — schedule one in Director's Mode.</div>
              ) : upcoming.map((s) => (
                <Link
                  key={s.id}
                  to={`/live/${s.id}`}
                  data-testid={`blueprint-upcoming-row-${s.id}`}
                  className="group flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5 ring-1 ring-white/10 hover:ring-tv-accent/50 transition"
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-black shrink-0">
                    {s.thumbnail_url ? (
                      <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-yellow-900 to-black">
                        <Clock className="w-4 h-4 text-white/50" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm truncate group-hover:text-tv-accent transition">{s.title}</div>
                    <div className="text-[10px] text-white/50 flex items-center gap-2 mt-0.5">
                      <span className="font-bold uppercase tracking-wider">{s.creator_name || "Creator"}</span>
                      <span>·</span>
                      <span className="text-tv-accent font-bold">{fmtCountdown(s.scheduled_for)}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-tv-accent transition shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Updated every 25s</div>
          <Link to="/live" className="text-xs font-bold text-tv-accent hover:underline">See all live →</Link>
        </div>
      </div>
    </section>
  );
}
